import { LDClient } from 'launchdarkly-js-client-sdk'
import { RouteParams } from 'vue-router'

/// <reference types="vite/client" />

export interface Asset {
  filename: string
}

export interface ImportMeta {
  vitest?: unknown
  env: any
}

export interface AddressComponent {
  longName: string
  shortName: string
  types: string[]
}

export interface AddressAutocomplete {
  addressComponents: Array<AddressComponent>
  name: string
}

export interface AddressPrediction {
  placeId: string
  mainText: string
  secondaryText: string
}

export interface BillingForm {
  firstName: string
  lastName: string
  zipCode: string
  country: string
}

export interface UserLocation {
  lng: number
  lat: number
  countryCode: string
}

export enum UserType {
  New = 'new',
  Existing = 'existing'
}

export interface SurveyResponse {
  id: string
  answer: string
  property: string
}

export interface UserAddress {
  street1?: string
  street2?: string
  city?: string
  region?: string
  postalCode?: string
  country: string
  phone?: string
}

export interface BillingInfoResponse {
  paymentMethod: {
    lastFour: string
    cardType: string
    expMonth: number
    expYear: number
    object: string
  }
  address: UserAddress
}

export interface BillingInfo {
  lastFour: string
  expMonth: number
  expYear: number
  cardType: string
  type: string
  address: UserAddress
}

export interface User {
  email: string
  outputUid: number
  location?: UserLocation | null
  customerSurveyResponses: SurveyResponse[]
  billingInfo: BillingInfo | null
  firstName: string
  lastName: string
  type: UserType | null
}

export interface Output {
  user: User
}

export interface DataLayer {
  push(data: object): void
}

export interface Window {
  LDClient: LDClient
  Output: Output
  dataLayer: DataLayer
  grecaptcha: any
}

export interface Link {
  url: string
  target?: string
}

export interface SurveyActionItem {
  body: string
  ctaLink: Link
  ctaText: string
  heading: string
  image: Asset
  linkHostname: string
}

export interface SurveyActionContent {
  content: SurveyActionItem
}

export interface HotkeyKeyValuePair {
  action?: SurveyActionContent
  text?: string
  value: string
  label?: string
}

export interface SurveyAction {
  content: SurveyActionItem
  option: string
  surveyGroup?: string[]
  surveyReference?: string
  modalName?: string
}

export interface Survey {
  actions?: SurveyAction[]
  answers: HotkeyKeyValuePair[]
  id: string
  property?: string
  question: string
}

export interface SurveyAnswer {
  code?: string
  label?: string
  value: string
}

export interface ManagedSurveyResult {
  answers: SurveyAnswer[]
  question: string
  surveyId: string
  shortProperty: string
  sortBy?: string
}

export interface ManagedSurveyQueryResponse {
  content: ManagedSurveyResult
  uuid: string
}

export interface SurveyAndActionResponse {
  surveys: ManagedSurveyQueryResponse[]
  actions: any[]
}

export interface SurveyActionResponse {
  modalContent: string
  surveyGroup: string
  surveyOptionValue: string
}

export interface SurveyResponseMap {
  [key: string]: string
}

export enum SurveyOptionsSortBy {
  Random = 'random',
  Ranked = 'ranked'
}

export interface ReturnState {
  value: string
  text: string
  disabled?: boolean
}

export interface HotkeyDataset {
  hotkey: string
  text: string
}

export interface HotkeyElement extends Element {
  dataset: HotkeyDataset
  value: string
  click(): void
}

export interface PropKeyValue {
  prop: string
  value: string
}

export interface PlanCurrency {
  currency: string
  pricePerIntervalLength?: string
  setupFee?: number
  unitAmount: number
}

export interface Plan {
  autoRenew: boolean
  code: string
  currentlyOffered: boolean
  featureFilter?: string
  interval: string
  intervalLength: number
  intervalAsNoun: string
  name: string
  planCurrencies: PlanCurrency[]
  planProduct: string
  planSet: string
  tier?: string
  trialLength: number
  trialRequiresBillingInfo: boolean
  trialUnit: string
}

export interface CustomField {
  name: string
  value: string
}

export interface PlanResult {
  autoRenew: boolean
  code: string
  currencies: Array<PlanCurrency>
  currentlyOffered: boolean
  customFields: Array<CustomField>
  intervalLength: number
  name: string
  planProduct: string
  planSet: string
  trialLength: number
  trialUnit: string
  trialRequiresBillingInfo: boolean
}

export enum ViewName {
  Create = 'create',
  Logout = 'logout',
  Login = 'login',
  Success = 'success'
}

export enum ScreenName {
  BillingAddress = 'billing-address',
  PaymentMethod = 'payment-method',
  PlanSelection = 'plan-selection',
  Survey = 'survey',
  Offer = 'offer',
  Upgrade = 'upgrade',
  Confirmation = 'confirmation',
  NoPayPlanPaymentMethod = 'no-pay-plan-payment-method',
  NoPayPlanConfirmation = 'no-pay-plan-confirmation',
  UpgradeConfirmation = 'upgrade-confirmation',
  OfferConfirmation = 'offer-confirmation',
  Existing = 'existing',
  TrialTimeline = 'trial-timeline'
}

export enum ScreenSetId {
  Checkout = 'checkout',
  Upgrade = 'upgrade',
  Offer = 'offer',
  NoPayPlan = 'no-pay-plan',
  Existing = 'existing'
}

export interface Screen {
  id: ScreenName
  name: string
  params?: RouteParams
  completed?: boolean
}

export interface ScreenSet {
  id: ScreenSetId
  screens: Screen[]
}

export interface ViewDecorations {
  badgeText?: string
  title?: string
  body?: string
  viewName?: ViewName
  offerMessage?: string
}

export enum ProductId {
  Arcade = 'arcade',
  OutputOne = 'output-one',
  CoProducer = 'co-producer',
  Creator = 'creator',
  OutputFx = 'output-fx'
}

export interface ManagedProductContentResult {
  rels: any[]
  story: any
}

export interface ManagedProductConfirmationVideo {
  description: string
  buttonText: string
  sectionAnchorLink: string
  videoUrl: Asset
  thumbnail?: Asset
  displayVideoControls: boolean
}

export interface ManagedProductContent {
  slug: string
  name: string
  confirmationVideo?: string
  productLogo?: {
    filename: string
  }
  checkoutSurveys: string
  confirmationFeaturesList: string[]
  nfrPlanSet: string
  subscriptionPlanSet: string
  productDownloads: string[]
}

export enum OfferProductId {
  CoProducerOutputFxPromo = 'co-pro-output-fx-promo',
  OutputOneResubscribePromo = 'output-one-resubscribe-promo'
}

export interface ManagedLink {
  url: string
}

export interface ManagedReleasePlatformResult {
  platform: string
  installer: ManagedLink
  updater: ManagedLink
  installerSize: string
  updaterSize: string
  minimum: string
}

export interface ManagedClientReleaseResult {
  releasePlatform: ManagedReleasePlatformResult[]
  version: string
  releaseNotes: string[]
}

export interface ManagedProductDownload {
  name: string
  method: string
  value: string
  managedClientRelease: string
  badge?: string
}

export interface ClientReleasePlatform {
  installer: string
  minimum?: string
  updater?: string
  version?: string
  installerSize: string
  updaterSize?: string
}

export interface ClientRelease {
  mac: ClientReleasePlatform
  win: ClientReleasePlatform
  notes?: string[]
  version?: string
}

export interface ClientReleases {
  [key: string]: ClientRelease
}

export interface InstallerServiceResponse extends ClientRelease {
  packages: ClientRelease
}

export interface ProductDownload {
  name: string
  id: string
  release: ClientRelease
}

export interface ProductDownloadMap {
  name: string
  id: string
  method: string
  value: string | ClientRelease
  badge?: string
}

export interface ProductConfirmationVideo {
  id: string
  buttonText: string
  description: string
  sectionAnchorLink: string
  url: string
  displayVideoControls: boolean
  thumbnail?: string
}

export interface Product {
  id: ProductId | string
  checkoutSurveys: string
  planSet: string
  nfrPlanSet: string
  name: string
  confirmationFeatures: string[]
  confirmationVideo?: any
  confirmationHeaderImage?: string
  releasesForDownload: ProductDownloadMap[]
  viewDecorations?: ViewDecorations[]
  showYamahaSignIn?: boolean
}

export enum SubscriptionState {
  Active = 'active',
  Canceled = 'canceled',
  Expired = 'expired',
  Paused = 'paused'
}

export interface Subscription {
  id: string
  state: SubscriptionState
  activatedAt: string
  trialEndsAt: string
  plan: Plan
  isPaused: boolean
}

export interface SubscriptionResult {
  id: string
  state: SubscriptionState
  activatedAt: string
  trialEndsAt: string
  pausedAt: string
  plan: {
    code: string
  }
}

export interface GeolocationCoordinates {
  lat: number
  lng: number
}

export interface GeolocationComponents {
  addressComponents: Array<AddressComponent>
  geometry: {
    location: GeolocationCoordinates
  }
  types: string[]
}

export interface PaymentMethodMap {
  [index: string]: string
}

export enum PaymentMethodType {
  creditCard = 'credit_card',
  payPal = 'paypal',
  cashApp = 'cashapp'
}

export interface PaymentMethodOption {
  id: string
  hotkey: string
  title: string
  value: PaymentMethodType
}

export interface CashAppInstance {
  // start() resolves once the underlying Adyen web component has mounted.
  start: () => Promise<void>
  destroy: () => void
  submit: () => void
  on: (event: 'token' | 'error' | 'cancel', handler: (payload: any) => void) => CashAppInstance
}

export interface CheckoutBillingAddress {
  firstName: string
  lastName: string
  country: string
  postalCode: string
}

export interface Checkout {
  firstName?: string
  lastName?: string
  planCode: string
  billingInfo: {
    tokenId?: string
    threeDSecureActionResultTokenId?: string
  }
  billingAddress: CheckoutBillingAddress | null
  country?: string
  currency: string
  email: string | null
  accountCode: string | null
  surveyResponses: SurveyResponseMap
  isActive: boolean
  isComplete: boolean
  hasCheckoutStart: string | null
  appReturnLink?: string | null
  subscriptionId?: string
  couponCodes?: string[]
  trialEndsAt?: string
  paymentDetails?: BillingInfo
  alreadyHas: boolean
  paymentRequired?: boolean
  noPaymentPlanType?: string
}

export interface ErrorDetails {
  field: string
  messages: string[]
}

export interface Error {
  code: string
  type?: string
  fields?: Array<string>
  details?: ErrorDetails
  message?: string
  transactionError?: {
    code?: string
    threeDSecureActionTokenId?: string
  }
}

export interface AppError {
  title?: string
  message?: string
}

export interface Modal {
  body?: string
  ctaLink?: Link
  ctaText?: string
  footer?: string
  id?: string
  image?: Asset
  isManagedContent?: boolean
  message?: string
  show?: boolean
  title: string
  modalName?: string
  hostname?: string
}

export interface ManagedModal {
  body: string
  cta: Link
  heading: string
  id: string
  image: string
}

export interface TokenPayload {
  id: string
  type: string
  card?: {
    brand: string
    last_four: string
    exp_month: number
    exp_year: number
  }
}

export interface TokenInput {
  checkoutForm: HTMLFormElement
  paymentMethod?: string
}

export interface BankInfoPayload {
  bank_name: string
}

export interface RecurlyAttributes {
  [key: string]: string
}

export interface RecurlyBillingInputGroup {
  form: BillingForm
  isUpdateAttempt: boolean
  paymentRequired: boolean
}

export interface InputText {
  inputId: string
  isDisabled?: boolean
  keepErrorsVisible?: boolean
  label: string
  message?: string
  modelValue: string
  placeholder?: string
  required?: boolean
  type?: string
  showAppend?: boolean
  validateInput?: () => void
  removeErrors?: () => void
  removeErrorsOnChange?: () => void
  setErrors?: () => void
  setErrorsOnChange?: () => void
}

export enum CouponState {
  Redeemable = 'redeemable',
  Expired = 'expired',
  MaxedOut = 'maxed-out'
}

export enum CouponDiscountType {
  Percent = 'percent',
  Trial = 'free_trial',
  Fixed = 'fixed' // Dollar off coupon type. Not currently supported.
}

export interface CouponCurrency {
  currency: string
  amount: string
}

export interface CouponUniqueCodeResponse {
  code: string
  state: CouponState
}

export interface CouponPlanResult {
  code: string
}

export interface Coupon {
  id: string
  code: string
  state?: CouponState
  discounted?: string
  discountType?: CouponDiscountType
  duration?: string
  temporalAmount?: number
  temporalUnit?: string
  name?: string
  percent?: number
  plans?: Plan[]
  appliesToAllPlans?: boolean
  trial?: {
    length: number
    unit: string
  }
  currencies?: CouponCurrency[]
}

export enum CouponDurationType {
  SingleUse = 'single_use',
  Temporal = 'temporal',
  Forever = 'forever'
}

export interface CouponResponse {
  id: string
  code: string
  state?: CouponState
  discounted?: string
  duration?: string
  durationType?: CouponDurationType
  temporalAmount?: number
  temporalUnit?: string
  name?: string
  discount: {
    currencies: CouponCurrency[]
    percent: number
    trial: {
      length: number
      unit: string
    }
    type?: CouponDiscountType
  }
  appliesToAllPlans: boolean
  plans: CouponPlanResult[]
  uniqueCouponCode: CouponUniqueCodeResponse | null
}

export enum ProductDownloadId {
  OutputOne = 'output-one',
  Arcade = 'arcade',
  CoProducer = 'co-producer',
  Creator = 'creator',
  Portal = 'portal',
  Movement = 'movement',
  Thermal = 'thermal'
}

export type FeatureFlagEvaluation = {
  id: string
  value: unknown
}
